import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import { toast } from "sonner";
import { Loader2, Phone, KeyRound, ArrowLeft } from "lucide-react";

/** Iter-274 W6 — SMS phone sign-in for accounts with a verified phone.
 *
 *  Backend contract:
 *    POST /auth/phone-login/request {phone}       → {sent, masked_phone}
 *      · 503 = Twilio creds not configured yet
 *      · 404 = no verified account with that number
 *      · 403 = suspended · 429 rate-limited · NZ mobiles only
 *    POST /auth/phone-login/confirm {phone, code} → full AuthResponse
 *      · 400 wrong/expired · 429 rate-limited
 */
function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return err?.message || fallback;
}

export default function PhoneLoginPage() {
  const { requestPhoneLogin, confirmPhoneLogin } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [phone, setPhone] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    if (step === "code" && codeRef.current) codeRef.current.focus();
  }, [step]);

  const onPhoneSubmit = async (e) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const r = await requestPhoneLogin(trimmed);
      setMaskedPhone(r?.masked_phone || trimmed);
      setStep("code");
      toast.success(`We sent a code to ${r?.masked_phone || trimmed}.`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.info("SMS sign-in is coming soon — try email or Google for now.");
      } else if (status === 404) {
        toast.error(errMsg(err, "No verified account with that phone. Verify your phone in Profile first to enable phone sign-in."));
      } else if (status === 429) {
        toast.error("Too many requests. Wait a moment and try again.");
      } else {
        toast.error(errMsg(err, "Could not send code"));
      }
    } finally {
      setLoading(false);
    }
  };

  const onCodeSubmit = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the 6-digit code we sent"); return; }
    setLoading(true);
    try {
      await confirmPhoneLogin(phone.trim(), code.trim());
      toast.success("Signed in.");
      nav(next, { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Verification failed"));
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      const r = await requestPhoneLogin(phone.trim());
      setMaskedPhone(r?.masked_phone || phone.trim());
      toast.success(`Code resent to ${r?.masked_phone || phone.trim()}`);
    } catch (err) {
      toast.error(errMsg(err, "Could not resend code"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-12">
      <Seo title="Sign in with phone" />
      <div className="rounded-3xl bg-white shadow-card p-8">
        <Link to={`/login${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[color:var(--brand-blue)] mb-4" data-testid="phone-login-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to email login
        </Link>

        {step === "phone" && (
          <>
            <h1 className="font-display text-3xl font-bold text-slate-900">Sign in with your phone</h1>
            <p className="mt-1 text-sm text-slate-500">We&apos;ll text a 6-digit code to your verified NZ mobile.</p>
            <form onSubmit={onPhoneSubmit} className="mt-6 space-y-4" data-testid="phone-login-request-form">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Mobile number</span>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="phone-login-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    placeholder="021 555 0100"
                    className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                  />
                </div>
              </label>
              <button
                data-testid="phone-login-request-submit"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Text me a code
              </button>
              <p className="text-[11px] text-slate-400">
                Only accounts with a verified phone can sign in this way. Verify your phone in Profile if you haven&apos;t already.
              </p>
            </form>
          </>
        )}

        {step === "code" && (
          <>
            <button type="button" onClick={() => { setStep("phone"); setCode(""); }} data-testid="phone-login-change-number" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[color:var(--brand-blue)] mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> Change number
            </button>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Enter your code</h1>
            <p className="mt-1 text-sm text-slate-500">
              We sent a 6-digit code to <span className="font-semibold text-slate-800" data-testid="phone-login-masked">{maskedPhone}</span>.
            </p>
            <form onSubmit={onCodeSubmit} className="mt-6 space-y-4" data-testid="phone-login-confirm-form">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Verification code</span>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    ref={codeRef}
                    data-testid="phone-login-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-lg tracking-[0.5em] text-center font-bold"
                    placeholder="••••••"
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-end text-[11px]">
                  <button type="button" onClick={onResend} disabled={resending} data-testid="phone-login-resend" className="font-semibold text-[color:var(--brand-blue)] hover:underline disabled:opacity-60">
                    {resending ? "Sending…" : "Resend code"}
                  </button>
                </div>
              </label>
              <button data-testid="phone-login-confirm-submit" type="submit" disabled={loading || !code} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 disabled:opacity-60 transition-colors">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Verify &amp; log in
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
